import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Switch } from '@/components/ui/Switch';
import { LoadingState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { keys } from '@/lib/queryKeys';
import { toast } from '@/hooks/useToast';
import { fetchProduct, updateProduct, type ProductDetail, type ProductPatch } from './api';
import { useVendors } from './useVendors';

const GST_OPTIONS = [0, 5, 12, 18, 28];
const DISCREPANCY: Record<string, string> = {
  short_supply: 'short', damaged: 'damaged', excess: 'excess', wrong_item: 'wrong item', other: 'other',
};

type Draft = {
  brand_name: string; strength: string; dosage_form: string; hsn_code: string; gst_percent: string; schedule: string;
  category: string; pack_size: string; preferred_vendor_id: string; active: boolean;
};

const toDraft = (p: ProductDetail): Draft => ({
  brand_name: p.brand_name ?? '', strength: p.strength ?? '', dosage_form: p.dosage_form ?? '', hsn_code: p.hsn_code ?? '',
  gst_percent: p.gst_percent != null ? String(Number(p.gst_percent)) : '12', schedule: p.schedule || 'OTC',
  category: p.category ?? '', pack_size: p.pack_size != null ? String(p.pack_size) : '',
  preferred_vendor_id: p.preferred_vendor_id != null ? String(p.preferred_vendor_id) : '', active: p.active,
});

/** Only the fields that changed, in the API's shape. */
function diff(p: ProductDetail, d: Draft): ProductPatch {
  const out: ProductPatch = {};
  const was = toDraft(p);
  const str = (v: string) => (v.trim() === '' ? null : v.trim());
  if (d.brand_name !== was.brand_name) out.brand_name = str(d.brand_name);
  if (d.strength !== was.strength) out.strength = str(d.strength);
  if (d.dosage_form !== was.dosage_form) out.dosage_form = str(d.dosage_form);
  if (d.hsn_code !== was.hsn_code) out.hsn_code = str(d.hsn_code);
  if (d.gst_percent !== was.gst_percent) out.gst_percent = Number(d.gst_percent);
  if (d.schedule !== was.schedule) out.schedule = d.schedule;
  if (d.category !== was.category) out.category = str(d.category);
  if (d.pack_size !== was.pack_size) out.pack_size = d.pack_size.trim() === '' ? null : Number(d.pack_size);
  if (d.preferred_vendor_id !== was.preferred_vendor_id) out.preferred_vendor_id = d.preferred_vendor_id ? Number(d.preferred_vendor_id) : null;
  if (d.active !== was.active) out.active = d.active;
  return out;
}

const money = (v: string | null | undefined) => (v == null ? '—' : `₹${Number(v).toFixed(2)}`);
const day = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/**
 * One product from the medicine master (Phase 1 G10): details, stock by store at
 * this branch, and every recent receipt (purchase and price history). Editable
 * by store staff and formulary admins; the generic name and clinical drug are
 * changed through Admin → Drug Review instead.
 */
export function ProductSheet({ medicineId, canEdit, onClose }: { medicineId: number | null; canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: keys.pharmacy.product(medicineId ?? 0), queryFn: () => fetchProduct(medicineId!), enabled: medicineId != null });
  const vendors = useVendors();
  const p = query.data;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  useEffect(() => { if (p) setDraft(toDraft(p)); }, [p]);
  useEffect(() => { if (medicineId == null) { setDraft(null); setFieldError(null); } }, [medicineId]);

  const save = useMutation({
    mutationFn: (patch: ProductPatch) => updateProduct(medicineId!, patch),
    onSuccess: () => {
      toast({ title: 'Product saved', variant: 'success' });
      qc.invalidateQueries({ queryKey: keys.pharmacy.product(medicineId!) });
      qc.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
    },
    onError: (e) => setFieldError(e instanceof ApiError || e instanceof Error ? e.message : 'Not saved'),
  });

  const patch = p && draft ? diff(p, draft) : {};
  const dirty = Object.keys(patch).length > 0;
  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setDraft((d) => (d ? { ...d, [k]: e.target.value } : d));
  const submit = () => {
    if (!draft) return;
    if (draft.pack_size.trim() && !(Number.isInteger(Number(draft.pack_size)) && Number(draft.pack_size) > 0)) {
      setFieldError('Pack size must be a whole number of units'); return;
    }
    setFieldError(null);
    save.mutate(patch);
  };

  return (
    <Sheet open={medicineId != null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="sm:max-w-[560px]">
        <SheetHeader>
          <SheetTitle>{p ? `${p.generic_name}${p.strength ? ` ${p.strength}` : ''}` : 'Product'}</SheetTitle>
          <SheetDescription>
            {p ? [p.brand_name, p.manufacturer_name, p.drug_name ? `Drug master: ${p.drug_name}` : 'Not yet reviewed in Drug Review'].filter(Boolean).join(' · ') : ''}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          {!p || !draft ? <LoadingState /> : (
            <div className="flex flex-col gap-5 text-[13px]">
              <section className="grid grid-cols-2 gap-x-3 gap-y-2">
                <FormField label="Brand name"><Input value={draft.brand_name} onChange={set('brand_name')} disabled={!canEdit} /></FormField>
                <FormField label="Strength"><Input value={draft.strength} onChange={set('strength')} disabled={!canEdit} /></FormField>
                <FormField label="Form"><Input value={draft.dosage_form} onChange={set('dosage_form')} disabled={!canEdit} placeholder="Tablet, Syrup…" /></FormField>
                <FormField label="Category" hint="How you group products, e.g. Antibiotic, Surgical consumable.">
                  <Input value={draft.category} onChange={set('category')} disabled={!canEdit} />
                </FormField>
                <FormField label="Pack size (units)" hint="Units per strip or pack; pre-fills goods receipts.">
                  <Input type="number" min={1} value={draft.pack_size} onChange={set('pack_size')} disabled={!canEdit} />
                </FormField>
                <FormField label="Preferred supplier">
                  <NativeSelect value={draft.preferred_vendor_id} onChange={set('preferred_vendor_id')} disabled={!canEdit}>
                    <option value="">None</option>
                    {(vendors.data ?? []).filter((v) => v.active !== false || String(v.id) === draft.preferred_vendor_id)
                      .map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </NativeSelect>
                </FormField>
                <FormField label="GST %">
                  <NativeSelect value={draft.gst_percent} onChange={set('gst_percent')} disabled={!canEdit}>
                    {[...new Set([...GST_OPTIONS.map(String), draft.gst_percent])].map((g) => <option key={g} value={g}>{g}%</option>)}
                  </NativeSelect>
                </FormField>
                <FormField label="HSN code"><Input value={draft.hsn_code} onChange={set('hsn_code')} disabled={!canEdit} /></FormField>
                <FormField label="Schedule">
                  <NativeSelect value={draft.schedule} onChange={set('schedule')} disabled={!canEdit}>
                    {['OTC', 'H', 'H1', 'X'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </NativeSelect>
                </FormField>
                <FormField label="Status" hint={draft.active ? 'Offered for prescribing and sale.' : 'Hidden from new prescriptions and sales; existing stock stays sellable.'}>
                  <label className="flex h-9 items-center gap-2">
                    <Switch checked={draft.active} onCheckedChange={(v) => setDraft((d) => (d ? { ...d, active: v } : d))} disabled={!canEdit} aria-label="Active" />
                    {draft.active ? 'Active' : 'Inactive'}
                  </label>
                </FormField>
              </section>
              {fieldError && <div className="rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{fieldError}</div>}

              <section>
                <div className="mb-1.5 font-semibold text-ink">Stock at this branch</div>
                {p.stock_by_store.length === 0 ? <div className="text-ink-muted">No stock recorded.</div> : (
                  <ul className="divide-y divide-border rounded-[var(--radius)] border border-border">
                    {p.stock_by_store.map((s) => (
                      <li key={s.store_id} className="flex flex-wrap justify-between gap-2 px-3 py-1.5">
                        <span className="font-medium">{s.store_name}</span>
                        <span className="text-ink-muted">
                          <b className="text-ink">{s.units}</b> units in {s.batches} batch(es) · earliest expiry {day(s.earliest_expiry)}
                          {s.min_level != null && ` · reorder at ${s.min_level}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <div className="mb-1.5 flex items-baseline justify-between">
                  <span className="font-semibold text-ink">Purchase history</span>
                  {p.last_purchase && (
                    <span className="text-xs text-ink-muted">Last cost {money(p.last_purchase.unit_landed_cost)}/unit · MRP {money(p.last_purchase.mrp)}</span>
                  )}
                </div>
                {p.purchases.length === 0 ? <div className="text-ink-muted">No goods receipts yet at this branch.</div> : (
                  <ul className="divide-y divide-border rounded-[var(--radius)] border border-border text-[12px]">
                    {p.purchases.map((r) => (
                      <li key={r.id} className="px-3 py-1.5">
                        <div className="flex flex-wrap justify-between gap-2">
                          <span className="font-medium">{day(r.received_at)} · {r.vendor_name ?? 'Unknown supplier'}</span>
                          <span>{money(r.unit_landed_cost)}/unit · MRP {money(r.mrp)}</span>
                        </div>
                        <div className="text-ink-muted">
                          {r.grn_number}{r.vendor_invoice_number ? ` · inv ${r.vendor_invoice_number}` : ''} · batch {r.batch_number} · exp {day(r.expiry_date)}
                          {r.mfg_date && ` · mfg ${day(r.mfg_date)}`} · {r.quantity_received} units
                          {r.invoiced_quantity != null && r.invoiced_quantity !== r.quantity_received && (
                            <Badge variant="warn" className="ml-1.5">invoiced {r.invoiced_quantity}{r.discrepancy_reason ? `, ${DISCREPANCY[r.discrepancy_reason] ?? r.discrepancy_reason}` : ''}</Badge>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </SheetBody>
        {canEdit && p && (
          <SheetFooter>
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button onClick={submit} disabled={!dirty} loading={save.isPending}>Save changes</Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
